import { RefCallback, useCallback, useRef } from "react";

export interface UseResizeSensorArgs {
  onResize: (entries: ResizeObserverEntry[]) => void;
}

/**
 * This hook allows for responding to a specifc DOM element resizing.
 * Event based so should be performant. Inspired by the `ResizeSensor`
 * from blueprint https://blueprintjs.com/docs/#core/components/resize-sensor.
 *
 * It's important that the passed callback is either stable or memoized with
 * infrequent changes.
 *
 * @param onResize callback invoked when the watched element resizes
 *
 * @returns ref callback that must be attached to the watched element
 */
export function useResizeSensor({
  onResize,
}: UseResizeSensorArgs): RefCallback<HTMLElement> {
  const resizeObserverRef = useRef<ResizeObserver>();

  // note that if this function reference changes, react calls the old
  // version of this function with `null` to clear it up and invokes the new version
  // with the instance making this similiar to `useEffect` in terms of behaviour
  // https://reactjs.org/docs/refs-and-the-dom.html#caveats-with-callback-refs
  return useCallback(
    (instance) => {
      if (instance != null) {
        // new element or dep change
        const newObserver = new ResizeObserver((entries) =>
          // using requestAnimationFrame to limit calling this function
          // may help prevent `ResizeObserver loop limit exceeded` errors
          requestAnimationFrame(() => onResize(entries)),
        );
        newObserver.observe(instance);
        resizeObserverRef.current = newObserver;
      } else {
        // cleanup of old resize observer
        const oldObserver = resizeObserverRef.current;
        oldObserver?.disconnect();
        resizeObserverRef.current = undefined;
      }
    },
    [onResize],
  );
}
